"use client";

import { Stack, Box, useMediaQuery, useTheme } from "@mui/material";
import React, { useState, useEffect } from "react";
import { ColletteCarousel } from "@collette-travel/collette-common-controls";
import CampaignCardCarouselSkeleton from "@/components/homepage/campaign-cards/campaign-card-carousel.skeleton";
import { CardGrid } from "@/types/dto/homepage/homepage-types";
import { SectionHeader } from "../../common/section-header";
import CampaignCards from "./campaign-card";

export interface CampaignCardCarouselProps {
  campaignSection: CardGrid;
  moduleName?: string;
  moduleRankingPosition?: string;
}

// eslint-disable-next-line react/prop-types
const CampaignCardCarouselSection: React.FC<CampaignCardCarouselProps> = ({ campaignSection, moduleName, moduleRankingPosition }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:1200px)");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 10);
    return () => clearTimeout(timer);
  }, [campaignSection]);

  const count = campaignSection.cards.length;

  // Determine arrow position based on card count and viewport
  const getArrowPosition = (): "bottom" | "top-right" | "none" | "sides" => {
    if (count === 1) return "none"; // 1 card: never show arrows at any viewport
    if (isMobile) return "bottom";
    if (count <= 3) return "none"; // 2 or 3 cards: no carousel on desktop
    return "top-right"; // 4+ cards: carousel enabled
  };
  const arrowPosition = getArrowPosition();

  // Determine container class based on card count
  const getContainerClassName = (): string | undefined => {
    if (count === 1) return "full-width-container";
    if (count === 2 && !isMobile) return "full-width-container";
    if (count >= 3 && !isMobile) return "fluid-box";
    return undefined;
  };

  // For 2 cards: use direct flex layout from 1200px down to 900px
  const isDesktopToTablet = useMediaQuery(theme.breakpoints.between(900, 1200));
  const useTwoCardLayout = count === 2 && (isDesktopToTablet || !isMobile);
  // For 3 cards on desktop, use direct flex layout with fluid-box for edge-to-edge
  const useThreeCardLayout = count === 3 && !isMobile;

  let variant: "full-width" | "multi-card" = "multi-card";
  if (count === 1) {
    variant = "full-width";
  }

  const cards = [...campaignSection.cards];
  if (useThreeCardLayout && cards.length === 3) {
    cards.unshift(cards.pop()!);
  }

  return (
    <Stack
      component="section"
      data-track="visibility"
      data-parent-container-type="carousel"
      data-module-name={moduleName || "Campaign Card Carousel Section"}
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition || "2"}
      data-carousel-card-name={campaignSection.basicInfo.headerText}
      data-carousel-position="2"
      className={getContainerClassName()}
      sx={
        count === 1
          ? { padding: { xs: "40px 24px", md: "40px 100px", xl: "40px 0" }, width: "100%" }
          : { padding: { xs: "40px 0", md: "40px 0" } } // No side padding on mobile for carousel edge-to-edge effect
      }
    >
      <SectionHeader
        sx={{ alignContent: "center", padding: { xs: "0 24px 30px", md: "0 0 30px" } }}
        headerText={campaignSection.basicInfo.headerText}
        headerSx={{ textAlign: "center", textTransform: "none !important" }}
        headerTestId="campaign-card-carousel-header"
        copyText={campaignSection.basicInfo.copyText?.replace(/<p>/g, "").replace(/<\/p>/g, "")}
        copySx={{ textAlign: "center", maxWidth: "650px", margin: "0 auto !important" }}
        copyTestId="campaign-card-carousel-subheader"
      />
      <CampaignCardCarouselSkeleton count={count} isLoading={isLoading} />

      {/* For 2 cards: use direct flex layout from 1200px down to 900px */}
      {useTwoCardLayout && (
        <Box
          data-testid="campaign-card-carousel"
          sx={{
            display: isLoading ? "none" : "flex",
            justifyContent: "center",
            gap: 3,
            paddingX: 3, // Match gap spacing on edges
            width: "100%"
          }}
        >
          {cards.map((card, index) => (
            <CampaignCards card={card} key={card.id} count={count} cardIndex={index} />
          ))}
        </Box>
      )}

      {/* For 3 cards on desktop: use fluid-box with paddingX to prevent edge clipping */}
      {useThreeCardLayout && (
        <Box
          className="fluid-box"
          data-testid="campaign-card-carousel"
          sx={{
            display: isLoading ? "none" : "flex",
            justifyContent: "center",
            gap: 3,
            paddingX: 3, // Same as gap - prevents cards from touching edges
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          {cards.map((card, index) => (
            <CampaignCards card={card} key={card.id} count={count} cardIndex={index} />
          ))}
        </Box>
      )}

      {/* For 1 card or 4+ cards, or mobile: use carousel */}
      {!useTwoCardLayout && !useThreeCardLayout && (
        <ColletteCarousel
          testId="campaign-card-carousel"
          sideBlur={false}
          variant={variant}
          arrowPosition={arrowPosition}
          align="center"
          slidesToScroll={1}
          spacing={3}
          loop
          carouselTrackSx={count === 3 && !isMobile ? { justifyContent: "center" } : {}}
          arrowContainerSx={{ marginRight: "70px" }}
          carouselRootSx={{ display: isLoading ? "none" : "inline" }}
        >
          {cards.map((card, index) => (
            <CampaignCards card={card} key={card.id} count={count} cardIndex={index} />
          ))}
        </ColletteCarousel>
      )}
    </Stack>
  );
};
export default CampaignCardCarouselSection;
